package com.trevorwiebe.timesheet.core.presentation

import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * App-shell ViewModel backing chrome that lives outside any single screen
 * (the side navigation rail/drawer): current-user role gating and sign-out.
 */
class AppViewModel(
    private val coreRepository: CoreRepository,
) : DisposableViewModel() {

    private val _state = MutableStateFlow(AppState())
    val state = _state.asStateFlow()

    private val _onSignOutChannel = Channel<Unit>()
    val onSignOutChannel = _onSignOutChannel.receiveAsFlow()

    private var userLoadInFlight = false

    fun onEvent(event: AppEvent) {
        when (event) {
            is AppEvent.OnRefreshUser -> loadUserIfNeeded()
            is AppEvent.OnShowConfirmSignOutDialog -> {
                _state.update { it.copy(confirmSignOutDialog = event.show) }
            }
            is AppEvent.OnSignOut -> signOut()
        }
    }

    // Called when a signed-in screen becomes current. The ViewModel is created
    // before sign-in, when the user document isn't readable yet, so the load is
    // deferred and retried until it succeeds.
    private fun loadUserIfNeeded() {
        if (_state.value.currentUser != null || userLoadInFlight) return
        userLoadInFlight = true
        viewModelScope.launch {
            val result = coreRepository.getDatabaseUser()
            val user = result.data as? DatabaseUserModel
            if (result.error == null && user != null) {
                _state.update { it.copy(currentUser = user) }
            }
            userLoadInFlight = false
        }
    }

    private fun signOut() {
        viewModelScope.launch {
            FirestoreListenerRegistry.clearAllListeners()
            val result = coreRepository.signOut()
            if (result.error == null) {
                _state.update { it.copy(confirmSignOutDialog = false, currentUser = null) }
                _onSignOutChannel.send(Unit)
            }
        }
    }
}

data class AppState(
    val currentUser: DatabaseUserModel? = null,
    val confirmSignOutDialog: Boolean = false,
)

sealed interface AppEvent {
    data object OnRefreshUser : AppEvent
    data class OnShowConfirmSignOutDialog(val show: Boolean) : AppEvent
    data object OnSignOut : AppEvent
}
